import type { UserSimpleResponse } from '@/apis/users-paginated';
import { formatPersonName } from '@/utils/nameFormatting';

export type UserSearchField = 'all' | 'name' | 'email' | 'client_id' | 'phone';

export const USER_SEARCH_FIELDS: Array<{ id: UserSearchField; label: string }> = [
  { id: 'all', label: 'All fields' },
  { id: 'name', label: 'Name' },
  { id: 'email', label: 'Email' },
  { id: 'client_id', label: 'Client ID' },
  { id: 'phone', label: 'Phone' },
];

export const getUserSearchTerms = (query: string) =>
  query.toLowerCase().trim().split(/\s+/).filter(Boolean);

export const getPrimaryUserSearchTerm = (query: string) => getUserSearchTerms(query)[0];

/** Same title Hub uses: first+last, else email local-part (`bio2@…` → `Bio2`). */
export const getUserSimpleDisplayName = (user: UserSimpleResponse) => {
  const name = [user.Profile?.first_name, user.Profile?.last_name]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');
  if (name) return formatPersonName(name);
  const local = (user.Profile?.email || user.email || '').split('@')[0].replace(/_/g, ' ').trim();
  if (local) return formatPersonName(local);
  return user.Username || 'Unknown';
};

export const getUserSearchableText = (user: UserSimpleResponse, field: UserSearchField = 'all') => {
  const profile = user.Profile;
  const firstName = (profile?.first_name ?? '').toLowerCase();
  const lastName = (profile?.last_name ?? '').toLowerCase();
  const fullName = `${firstName} ${lastName}`.trim();
  const email = (profile?.email ?? user.email ?? '').toLowerCase();
  const phone = (profile?.phone_number ?? '').toLowerCase();
  const clientId = (profile?.client_id ?? '').toLowerCase();

  if (field === 'name') {
    return [fullName, getUserSimpleDisplayName(user).toLowerCase()].filter(Boolean).join(' ');
  }
  if (field === 'email') return email;
  if (field === 'phone') return phone;
  if (field === 'client_id') return clientId;
  return [fullName, email, phone, clientId].filter(Boolean).join(' ');
};

export const matchesUserSearch = (
  user: UserSimpleResponse,
  query: string,
  field: UserSearchField = 'all'
) => {
  const terms = getUserSearchTerms(query);
  if (terms.length === 0) return true;

  const searchableText = getUserSearchableText(user, field);
  return terms.every((term) => searchableText.includes(term));
};

/**
 * Filters users by multi-term search. Each whitespace-separated term must match
 * the selected field (name, email, phone, Client ID, or all of them).
 */
export const filterUsersBySearch = (
  users: UserSimpleResponse[],
  query: string,
  field: UserSearchField = 'all'
) => {
  if (!query.trim()) return users;
  return users.filter((user) => matchesUserSearch(user, query, field));
};
